import { useScrollAnimation } from "../hooks/useScrollAnimation";

interface ApplicationsProps {
  product: any;
}

export default function Applications({ product }: ApplicationsProps) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      ref={ref}
      id="applications"
      className="py-8 sm:py-10 md:py-12 bg-gradient-to-r from-stone-700 via-stone-700 to-stone-700 rounded-2xl md:rounded-3xl shadow-lg dark:shadow-2xl dark:shadow-stone-900/50 mx-4 sm:mx-6"
    >
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}>
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-100 mb-3 sm:mb-4 dark:text-gray-50">
            Applications
          </h2>
          <div className="w-16 sm:w-20 md:w-24 h-0.5 bg-sky-400 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
          {product.applications.map((app: any, index: number) => (
            <div
              key={index}
              className="group bg-stone-800 p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl hover:bg-stone-700/80 transition-all duration-500 hover:scale-[1.02] sm:hover:scale-105 cursor-pointer border border-stone-700 hover:border-sky-500/70 shadow-sm hover:shadow-xl dark:shadow-stone-900/50"
              style={{
                animationDelay: `${index * 100}ms`,
                animation: isVisible ? `fadeInUp 0.6s ease-out ${index * 100}ms both` : 'none',
              }}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-sky-900/20 transition-colors overflow-hidden bg-stone-900/50">
                  <img
                    src={app.image}
                    alt={app.name}
                    className="w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const fallbackIcon = document.createElement("div");
                      fallbackIcon.className =
                        "w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-sky-500 rounded-full";
                      e.currentTarget.parentElement?.appendChild(
                        fallbackIcon
                      );
                    }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-100 group-hover:text-sky-400 transition-colors dark:text-gray-50">
                    {app.name}
                  </h3>
                  {app.description && (
                    <p className="text-xs sm:text-sm text-gray-300 mt-1 line-clamp-2">
                      {app.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add responsive keyframes for animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Mobile-specific adjustments */
        @media (max-width: 640px) {
          .grid {
            max-width: 400px;
            margin-left: auto;
            margin-right: auto;
          }
        }
      `}</style>
    </section>
  );
}